import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Pagination,
  Grid,
  CircularProgress,
  Tooltip,
  Autocomplete,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ClearIcon from '@mui/icons-material/Clear';
import SearchIcon from '@mui/icons-material/Search';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { hasPermission } from '../../../../utils/permissionUtils';
import { LIFECYCLE_EVENT_TYPES, formatEnumLabel } from '../../../../constants/hrm';
import { useLifecycleEvents, useCreateLifecycleEvent, useEmployees, useDepartments, usePositions } from '../../../../hooks/useHRM';

const eventTypeColors = {
  HIRED: 'success',
  PROMOTED: 'info',
  TRANSFERRED: 'primary',
  TERMINATED: 'error',
  RESIGNED: 'warning',
  ON_LEAVE: 'warning',
  RETURNED: 'success',
  PROBATION_END: 'info',
  SALARY_CHANGE: 'secondary',
  ROLE_CHANGE: 'primary',
  OTHER: 'default',
};

const initialFormData = {
  employee_id: '',
  event_type: '',
  event_date: '',
  description: '',
  from_department_id: '',
  to_department_id: '',
  from_position_id: '',
  to_position_id: '',
  notes: '',
};

const EmployeeLifecycle = () => {
  const { chatbotId } = useParams();

  const [page, setPage] = useState(1);

  const [filterEmployee, setFilterEmployee] = useState('');
  const [filterEventType, setFilterEventType] = useState('');

  const [employeeSearch, setEmployeeSearch] = useState('');

  const [dialogOpen, setDialogOpen] = useState(false);
  const [formData, setFormData] = useState(initialFormData);
  const [formErrors, setFormErrors] = useState({});
  const [selectedEmployee, setSelectedEmployee] = useState(null);

  const [canView, setCanView] = useState(false);
  const [canModify, setCanModify] = useState(false);

  useEffect(() => {
    const checkPermissions = async () => {
      const view = await hasPermission('hrm', 'view');
      const modify = await hasPermission('hrm', 'add|edit');
      setCanView(view);
      setCanModify(modify);
    };
    checkPermissions();
  }, []);

  // React Query hooks for data fetching
  const eventsFilters = {
    page,
    per_page: 20,
    ...(filterEmployee && { employee_id: filterEmployee }),
    ...(filterEventType && { event_type: filterEventType }),
  };
  const { data: eventsData, isLoading: loading } = useLifecycleEvents(chatbotId, eventsFilters, { enabled: !!chatbotId && canView });
  const events = eventsData?.data || [];
  const pagination = eventsData?.pagination || { total: 0, pages: 1, per_page: 20 };

  const { data: employeesData } = useEmployees(chatbotId, { per_page: 1000 }, { enabled: !!chatbotId && canView });
  const employees = employeesData?.data || [];

  const { data: departmentsData } = useDepartments(chatbotId, { per_page: 1000 }, { enabled: !!chatbotId && canView });
  const departments = departmentsData?.data || [];

  const { data: positionsData } = usePositions(chatbotId, { per_page: 1000 }, { enabled: !!chatbotId && canView });
  const positions = positionsData?.data || [];

  // Employee search for Autocomplete in dialog
  const employeeSearchFilters = employeeSearch && employeeSearch.length >= 2
    ? { search: employeeSearch, per_page: 20 }
    : { per_page: 20 };
  const { data: employeeSearchData, isLoading: employeeSearchLoading } = useEmployees(chatbotId, employeeSearchFilters, { enabled: !!chatbotId && canView });
  const employeeSearchResults = employeeSearchData?.data || (employeeSearch && employeeSearch.length >= 2 ? [] : employees.slice(0, 20));

  // Mutation for creating lifecycle events
  const createLifecycleEvent = useCreateLifecycleEvent(chatbotId);

  const clearFilters = () => {
    setFilterEmployee('');
    setFilterEventType('');
    setPage(1);
  };

  const handleOpenCreate = () => {
    setFormData(initialFormData);
    setFormErrors({});
    setSelectedEmployee(null);
    setEmployeeSearch('');
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setFormData(initialFormData);
    setFormErrors({});
    setSelectedEmployee(null);
  };

  const handleFormChange = (field) => (e) => {
    setFormData((prev) => ({ ...prev, [field]: e.target.value }));
    if (formErrors[field]) {
      setFormErrors((prev) => ({ ...prev, [field]: '' }));
    }
  };

  const handleEmployeeSelect = (event, value) => {
    setSelectedEmployee(value);
    setFormData((prev) => ({ ...prev, employee_id: value ? value.id : '' }));
    if (formErrors.employee_id) {
      setFormErrors((prev) => ({ ...prev, employee_id: '' }));
    }
  };

  const validateForm = () => {
    const errors = {};
    if (!formData.employee_id) errors.employee_id = 'Employee is required';
    if (!formData.event_type) errors.event_type = 'Event type is required';
    if (!formData.event_date) errors.event_date = 'Event date is required';
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) return;

    try {
      const payload = { ...formData };

      // Convert empty strings to null for optional fields
      if (!payload.description) payload.description = null;
      if (!payload.from_department_id) payload.from_department_id = null;
      if (!payload.to_department_id) payload.to_department_id = null;
      if (!payload.from_position_id) payload.from_position_id = null;
      if (!payload.to_position_id) payload.to_position_id = null;
      if (!payload.notes) payload.notes = null;

      await createLifecycleEvent.mutateAsync(payload);
      handleCloseDialog();
    } catch (error) {
      console.error('Error saving lifecycle event:', error);
      if (error.response?.data?.message) {
        setFormErrors({ general: error.response.data.message });
      }
    }
  };

  if (!canView) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="h6" color="error">
          You do not have permission to view lifecycle events.
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 2, mb: 3 }}>
        <Typography variant="h4" fontWeight="bold" sx={{ fontSize: { xs: '1.5rem', sm: '2.125rem' } }}>
          Employee Lifecycle Events
        </Typography>
        {canModify && (
          <Button variant="contained" startIcon={<AddIcon />} onClick={handleOpenCreate} size="small">
            Add Event
          </Button>
        )}
      </Box>

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={6} md={4}>
            <FormControl fullWidth size="small">
              <InputLabel>Employee</InputLabel>
              <Select
                value={filterEmployee}
                onChange={(e) => { setFilterEmployee(e.target.value); setPage(1); }}
                label="Employee"
              >
                <MenuItem value="">All Employees</MenuItem>
                {employees.map((emp) => (
                  <MenuItem key={emp.id} value={emp.id}>
                    {emp.first_name} {emp.last_name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={4} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Event Type</InputLabel>
              <Select
                value={filterEventType}
                onChange={(e) => { setFilterEventType(e.target.value); setPage(1); }}
                label="Event Type"
              >
                <MenuItem value="">All Types</MenuItem>
                {LIFECYCLE_EVENT_TYPES.map((type) => (
                  <MenuItem key={type} value={type}>
                    {formatEnumLabel(type)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={2} md={1}>
            <Tooltip title="Clear filters">
              <IconButton onClick={clearFilters}>
                <ClearIcon />
              </IconButton>
            </Tooltip>
          </Grid>
        </Grid>
      </Paper>

      {/* Events Table */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          <TableContainer component={Paper} sx={{ overflowX: 'auto' }}>
            <Table sx={{ minWidth: 700 }}>
              <TableHead>
                <TableRow>
                  <TableCell>Employee Name</TableCell>
                  <TableCell>Event Type</TableCell>
                  <TableCell>Event Date</TableCell>
                  <TableCell>Description</TableCell>
                  <TableCell>Department Change</TableCell>
                  <TableCell>Position Change</TableCell>
                  <TableCell>Notes</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {events.length > 0 ? (
                  events.map((event) => (
                    <TableRow key={event.id} hover>
                      <TableCell>
                        <Typography variant="body2" fontWeight="medium">
                          {event.employee_first_name} {event.employee_last_name}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={formatEnumLabel(event.event_type)}
                          color={eventTypeColors[event.event_type] || 'default'}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        {event.event_date
                          ? new Date(event.event_date).toLocaleDateString()
                          : '-'}
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {event.description || '-'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {event.from_department_name || event.to_department_name ? (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <Typography variant="body2">
                              {event.from_department_name || '-'}
                            </Typography>
                            <ArrowForwardIcon fontSize="small" color="action" />
                            <Typography variant="body2">
                              {event.to_department_name || '-'}
                            </Typography>
                          </Box>
                        ) : (
                          '-'
                        )}
                      </TableCell>
                      <TableCell>
                        {event.from_position_title || event.to_position_title ? (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <Typography variant="body2">
                              {event.from_position_title || '-'}
                            </Typography>
                            <ArrowForwardIcon fontSize="small" color="action" />
                            <Typography variant="body2">
                              {event.to_position_title || '-'}
                            </Typography>
                          </Box>
                        ) : (
                          '-'
                        )}
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {event.notes || '-'}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                      <Typography color="textSecondary">No lifecycle events found</Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {pagination.pages > 1 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
              <Pagination
                count={pagination.pages}
                page={page}
                onChange={(e, value) => setPage(value)}
                color="primary"
              />
            </Box>
          )}
        </>
      )}

      {/* Create Event Dialog */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>Create Lifecycle Event</DialogTitle>
        <DialogContent>
          {formErrors.general && (
            <Typography color="error" sx={{ mb: 2 }}>
              {formErrors.general}
            </Typography>
          )}
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6}>
              <Autocomplete
                options={employeeSearchResults}
                getOptionLabel={(option) =>
                  `${option.first_name} ${option.last_name}${option.employee_id_number ? ` (${option.employee_id_number})` : ''}`
                }
                value={selectedEmployee}
                onChange={handleEmployeeSelect}
                onInputChange={(event, newInputValue) => {
                  setEmployeeSearch(newInputValue);
                }}
                loading={employeeSearchLoading}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Employee"
                    required
                    error={!!formErrors.employee_id}
                    helperText={formErrors.employee_id}
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {employeeSearchLoading ? <CircularProgress size={20} /> : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth required error={!!formErrors.event_type}>
                <InputLabel>Event Type</InputLabel>
                <Select
                  value={formData.event_type}
                  onChange={handleFormChange('event_type')}
                  label="Event Type"
                >
                  {LIFECYCLE_EVENT_TYPES.map((type) => (
                    <MenuItem key={type} value={type}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Chip
                          label={formatEnumLabel(type)}
                          color={eventTypeColors[type] || 'default'}
                          size="small"
                        />
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
                {formErrors.event_type && (
                  <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 2 }}>
                    {formErrors.event_type}
                  </Typography>
                )}
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Event Date"
                type="date"
                value={formData.event_date}
                onChange={handleFormChange('event_date')}
                InputLabelProps={{ shrink: true }}
                required
                error={!!formErrors.event_date}
                helperText={formErrors.event_date}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Description"
                value={formData.description}
                onChange={handleFormChange('description')}
              />
            </Grid>

            {/* Department Change */}
            <Grid item xs={12}>
              <Typography variant="subtitle2" color="primary" fontWeight="bold" sx={{ mt: 1 }}>
                Department Change (optional)
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>From Department</InputLabel>
                <Select
                  value={formData.from_department_id}
                  onChange={handleFormChange('from_department_id')}
                  label="From Department"
                >
                  <MenuItem value="">None</MenuItem>
                  {departments.map((dept) => (
                    <MenuItem key={dept.id} value={dept.id}>
                      {dept.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>To Department</InputLabel>
                <Select
                  value={formData.to_department_id}
                  onChange={handleFormChange('to_department_id')}
                  label="To Department"
                >
                  <MenuItem value="">None</MenuItem>
                  {departments.map((dept) => (
                    <MenuItem key={dept.id} value={dept.id}>
                      {dept.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {/* Position Change */}
            <Grid item xs={12}>
              <Typography variant="subtitle2" color="primary" fontWeight="bold" sx={{ mt: 1 }}>
                Position Change (optional)
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>From Position</InputLabel>
                <Select
                  value={formData.from_position_id}
                  onChange={handleFormChange('from_position_id')}
                  label="From Position"
                >
                  <MenuItem value="">None</MenuItem>
                  {positions.map((pos) => (
                    <MenuItem key={pos.id} value={pos.id}>
                      {pos.title}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>To Position</InputLabel>
                <Select
                  value={formData.to_position_id}
                  onChange={handleFormChange('to_position_id')}
                  label="To Position"
                >
                  <MenuItem value="">None</MenuItem>
                  {positions.map((pos) => (
                    <MenuItem key={pos.id} value={pos.id}>
                      {pos.title}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {/* Notes */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Notes"
                value={formData.notes}
                onChange={handleFormChange('notes')}
                multiline
                rows={3}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button variant="contained" onClick={handleSave} disabled={createLifecycleEvent.isPending}>
            {createLifecycleEvent.isPending ? <CircularProgress size={24} /> : 'Create Event'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default EmployeeLifecycle;
